import React, { useEffect, useState, useRef } from 'react';
import { Menu, X, Youtube, Instagram, Facebook, Linkedin, Phone, Mail, ChevronDown } from 'lucide-react';
import { Button } from '../ui/Button';
const LOGO_URL = 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FRedesigne%202024%2FMTDLarge%201.png&site=1856&w=191&h=108&user=1516&opt=w3';
const socialLinks = [{
  icon: Youtube,
  href: 'https://www.youtube.com/channel/UCWe8nIgfZn-Lhny4k-Wn3Wg',
  label: 'YouTube'
}, {
  icon: Instagram,
  href: 'https://www.instagram.com/medinatactical360/',
  label: 'Instagram'
}, {
  icon: Facebook,
  href: 'https://www.facebook.com/TeamAPCUSA/',
  label: 'Facebook'
}, {
  icon: Linkedin,
  href: 'https://www.linkedin.com/in/jose-medina-a84845a/',
  label: 'LinkedIn'
}];
interface DropdownItem {
  name: string;
  href: string;
}
interface NavLink {
  name: string;
  href: string;
  children?: DropdownItem[];
}
function DropdownMenu({
  items,
  isOpen
}: {
  items: DropdownItem[];
  isOpen: boolean;
}) {
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 mt-1 w-56 bg-brand-gray-900 border border-brand-gray-700 shadow-xl z-50" data-id="element-50">
      {items.map(item => <a key={item.name} href={item.href} className="block px-4 py-3 text-xs font-medium text-brand-gray-300 hover:text-brand-teal hover:bg-brand-gray-800 uppercase tracking-wider transition-colors border-b border-brand-gray-800 last:border-0" data-id="element-51">
          {item.name}
        </a>)}
    </div>;
}
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const navLinks: NavLink[] = [{
    name: 'About Us',
    href: '/about-apc',
    children: [{
      name: 'Meet the Founder',
      href: '/meet-the-founder'
    }]
  }, {
    name: 'Crisis Planning',
    href: '/crisis-management-planning'
  }, {
    name: 'Law Enforcement',
    href: '/law-enforcement-capabilities'
  }, {
    name: 'Civilian',
    href: '/civilian-protection'
  }, {
    name: 'Corporate',
    href: '/corporate-critical-incidents-planning'
  }, {
    name: 'Training',
    href: '/training'
  }, {
    name: 'Resources',
    href: '/strategic-partners',
    children: [{
      name: 'Blog',
      href: '/blog'
    }]
  }];
  return <nav className="fixed top-0 left-0 right-0 z-50" ref={navRef} data-id="element-52">
      {/* ── Utility Bar ── */}
      <div className="bg-brand-gray-900 border-b border-brand-gray-800 hidden md:block" data-id="element-53">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-54">
          <div className="flex justify-between items-center h-9" data-id="element-55">
            <div className="flex items-center gap-4" data-id="element-56">
              <a href="tel:18666343373" className="flex items-center gap-1.5 text-xs text-brand-gray-400 hover:text-brand-teal transition-colors" data-id="element-57">
                <Phone className="w-3 h-3" data-id="element-58" />
                1-866-634-3373
              </a>
              <span className="w-px h-3 bg-brand-gray-700" data-id="element-59" />
              <a href="mailto:info@apc360zone.com" className="flex items-center gap-1.5 text-xs text-brand-gray-400 hover:text-brand-teal transition-colors" data-id="element-60">
                <Mail className="w-3 h-3" data-id="element-61" />
                info@apc360zone.com
              </a>
            </div>
            <div className="flex items-center gap-3" data-id="element-62">
              {socialLinks.map(social => <a key={social.label} href={social.href} target="_blank" rel="noreferrer" aria-label={social.label} className="text-brand-gray-500 hover:text-brand-teal transition-colors" data-id="element-63">
                  <social.icon className="w-3.5 h-3.5" data-id="element-64" />
                </a>)}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Nav ── */}
      <div className="bg-brand-black/95 backdrop-blur-md border-b border-brand-gray-800" data-id="element-65">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-66">
          <div className="flex justify-between items-center h-20" data-id="element-67">
            {/* Logo — left anchor */}
            <a href="/" className="flex-shrink-0 -ml-2" data-id="element-68">
              <img src={LOGO_URL} alt="Medina Tactical Dynamics" className="h-[4.25rem] w-auto" data-id="element-69" />
            </a>

            {/* Desktop Nav — centered between logo and button */}
            <div className="hidden lg:flex flex-1 items-center justify-center gap-5" data-id="element-70">
              {navLinks.map(link => <div key={link.name} className="relative flex items-center" data-id="element-71">
                  {link.children ? <button onClick={() => setOpenDropdown(openDropdown === link.name ? null : link.name)} className="flex items-center gap-1 text-[13px] font-medium text-brand-gray-300 hover:text-brand-teal uppercase tracking-wider transition-colors leading-none" data-id="element-72">
                      {link.name}
                      <ChevronDown className={`w-3 h-3 transition-transform flex-shrink-0 ${openDropdown === link.name ? 'rotate-180' : ''}`} data-id="element-73" />
                    </button> : <a href={link.href} className="text-[13px] font-medium uppercase tracking-wider transition-colors leading-none text-brand-gray-300 hover:text-brand-teal" data-id="element-74">
                      {link.name}
                    </a>}
                  {link.children && <DropdownMenu items={link.children} isOpen={openDropdown === link.name} data-id="element-75" />}
                </div>)}
            </div>

            {/* Contact Us — right anchor */}
            <div className="hidden lg:flex flex-shrink-0" data-id="element-76">
              <Button href="/Contact-Medina Tactical" variant="primary" size="sm" data-id="element-77">
                Contact Us
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center ml-auto" data-id="element-78">
              <button onClick={() => setIsOpen(!isOpen)} className="text-brand-gray-300 hover:text-brand-white focus:outline-none" data-id="element-79">
                {isOpen ? <X className="h-6 w-6" data-id="element-80" /> : <Menu className="h-6 w-6" data-id="element-81" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {isOpen && <div className="lg:hidden bg-brand-gray-900 border-b border-brand-gray-800" data-id="element-82">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3" data-id="element-83">
              {navLinks.map(link => <div key={link.name} data-id="element-84">
                  <a href={link.href} className="block px-3 py-2 text-sm font-medium uppercase tracking-wider transition-colors text-brand-gray-300 hover:text-brand-teal hover:bg-brand-gray-800" data-id="element-85">
                    {link.name}
                  </a>
                  {link.children?.map(child => <a key={child.name} href={child.href} className="block px-6 py-2 text-xs font-medium text-brand-gray-400 hover:text-brand-teal hover:bg-brand-gray-800 uppercase tracking-wider" data-id="element-86">
                      — {child.name}
                    </a>)}
                </div>)}
              <div className="px-3 py-2" data-id="element-87">
                <Button href="/Contact-Medina Tactical" variant="primary" className="w-full" data-id="element-88">
                  Contact Us
                </Button>
              </div>
              <div className="px-3 pt-4 mt-2 border-t border-brand-gray-800 flex items-center justify-between" data-id="element-89">
                <div className="flex items-center gap-4" data-id="element-90">
                  {socialLinks.map(social => <a key={social.label} href={social.href} target="_blank" rel="noreferrer" aria-label={social.label} className="text-brand-gray-500 hover:text-brand-teal transition-colors" data-id="element-91">
                      <social.icon className="w-4 h-4" data-id="element-92" />
                    </a>)}
                </div>
                <a href="tel:18666343373" className="text-xs text-brand-gray-400 hover:text-brand-teal transition-colors" data-id="element-93">
                  1-866-634-3373
                </a>
              </div>
            </div>
          </div>}
      </div>
    </nav>;
}